<i18n>
{
  "en": {
    "groupIndexingRule": "Index demands",
    "addRule": "Add demand indexing rule",
    "deleteTitle": "Are you sure you want to delete this demand indexing rule?",
    "deleteError": "There was an error deleting the demand indexing rule",
    "createError": "There was an error creating the demand relationship rule.",
    "conflictError": "There was an error with the chosen demands, check that the groups of products are correct.",
    "noRules": "No indexing rules available",
    "requestError": "There was an error loading needing data for demands",
    "delete": "Delete",
    "saveDelay": "This process may take a while. Please hold on until it finishes.",
    "editError": "There was an error updating the rule. Please, try it later."
  },
  "es": {
    "groupIndexingRule": "Relacionar demandas",
    "addRule": "Añadir regla de relación de demandas",
    "deleteTitle": "¿Estás seguro de que quieres borrar esta regla de relación de demandas?",
    "deleteError": "Ha habido un error borrando la regla de relación de demandas",
    "createError": "Ha habido un error creando la regla de relación de demandas",
    "conflictError": "Ha surgido un error con las demandas escogidas, compruebe que los grupos de productos son correctos.",
    "noRules": "No hay reglas disponibles",
    "requestError": "Ha habido un error cargando los datos de las demandas",
    "delete": "Borrar",
    "saveDelay": "Este proceso puede tardar un rato. Por favor, espere a que termine.",
    "editError": "Hubo un error editando la "
  }
}
</i18n>

<template>
  <v-container class="mb-10">
    <div class="d-flex justify-content-center">
      <div class="ml-auto text-center font-weight-bold ma-1 text-md-h6 text-body-1">
        {{ $t('groupIndexingRule') }}
      </div>
      <div class="ml-auto">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="mr-2" v-on="on" v-bind="attrs" @click="openCreateModal">
              <v-icon color="grey" large>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('addRule') }}</span>
        </v-tooltip>
      </div>
    </div>
    <dynameat-skeleton v-if="isLoading" skeleton-type="manualConfigList" :container="true"/>
    <div v-if="emptyRuleList && !isLoading" class="d-flex justify-space-around grey--text mt-5">
      <span>{{ $t('noRules') }}</span>
    </div>
    <v-list v-for="rule in groupIndexingRuleList" :key="rule.guid">
      <group-indexing-rule-card :rule="rule" @delete="onOpenDeleteModal(rule)" @edit="onEdit"/>
    </v-list>
    <group-indexing-rule-modal :visible="createModalVisible" @confirm="onConfirmCreateModal"
                               @cancel="onCancelCreateModal"/>
    <dynameat-confirmation-modal :visible="deleteModalVisible" :title="$t('deleteTitle')"
                                 :description="deleteName" :confirm-text="$t('delete')"
                                 @confirm="onConfirmDeleteModal" @cancel="onCloseDeleteModal"/>
  </v-container>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { createErrorNotification, createWarningNotification } from '@ulibs/unnotificationsqueue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import GroupIndexingRuleCard from './components/GroupIndexingRuleCard.vue';
import GroupIndexingRuleModal from './components/GroupIndexingRuleModal.vue';

@Component
@Components({
  GroupIndexingRuleModal, GroupIndexingRuleCard, DynameatConfirmationModal, DynameatSkeleton
})
@MapActions(['groupIndexingRuleListRequest', 'createGroupIndexingRuleRequest',
  'removeGroupIndexingRuleRequest', 'setLoading', 'addNotification',
  'updateGroupIndexingRuleRequest'])
@MapGetters(['groupIndexingRuleList', 'isLoading', 'groupList'])
export default class GroupIndexingRule extends Vue {
  createModalVisible = false;

  deleteModalVisible = false;

  ruleToDelete = null;

  get deleteName() {
    if (this.ruleToDelete) {
      const referenceGroupName = this.groupList.find((el) => el.code_name === this.ruleToDelete.referenceGroup)?.translation;
      const changedGroupName = this.groupList.find((el) => el.code_name === this.ruleToDelete.changedGroup)?.translation;
      const sum = (this.ruleToDelete.fixedIncrement || this.ruleToDelete.percentageIncrement) > 0 ? '+' : '';
      const incrementToShow = this.ruleToDelete.fixedIncrement !== null ? `${this.ruleToDelete.fixedIncrement}€` : `${this.ruleToDelete.percentageIncrement}%`;
      return `${referenceGroupName} -> ${changedGroupName} (${sum}${incrementToShow})`;
    }
    return '';
  }

  get emptyRuleList() {
    return this.groupIndexingRuleList.length < 1;
  }

  async created() {
    await this.getGroupIndexingRuleList();
  }

  async getGroupIndexingRuleList() {
    try {
      this.setLoading(1);
      await this.groupIndexingRuleListRequest(this.$route.params.pointOfSaleGuid);
    } catch {
      this.addNotification(createErrorNotification(this.$t('requestError')));
    } finally {
      this.setLoading(-1);
    }
  }

  async onEdit(rule) {
    try {
      await this.updateGroupIndexingRuleRequest(rule);
      this.addNotification(createWarningNotification(this.$t('saveDelay')));
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('editError'), null));
    }
  }

  openCreateModal() {
    this.createModalVisible = true;
  }

  onCancelCreateModal() {
    this.createModalVisible = false;
  }

  async onConfirmCreateModal(rule) {
    this.createModalVisible = false;
    try {
      await this.setLoading(1);
      this.addNotification(createWarningNotification(this.$t('saveDelay')));
      await this.createGroupIndexingRuleRequest(rule);
    } catch (err) {
      const message = Object.values(err.response.data || {});
      const msgKey = Object.keys(err.response.data || {});
      if (err.response.status === 409) {
        this.addNotification(createErrorNotification(this.$t(msgKey), null));
        this.addNotification(createErrorNotification(message, null, 10000));
      } else {
        this.addNotification(createErrorNotification(this.$t('createError'), null));
      }
    } finally {
      await this.setLoading(-1);
    }
  }

  onOpenDeleteModal(rule) {
    this.ruleToDelete = rule;
    this.deleteModalVisible = true;
  }

  onCloseDeleteModal() {
    this.ruleToDelete = null;
    this.deleteModalVisible = false;
  }

  async onConfirmDeleteModal() {
    this.deleteModalVisible = false;
    try {
      await this.setLoading(1);
      await this.removeGroupIndexingRuleRequest(this.ruleToDelete.guid);
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('deleteError'), null, 5000));
    } finally {
      await this.setLoading(-1);
    }
  }
}

</script>
